// Shared "Not enough balance" prompt — used by every game surface
// (Ludo + X&O + future). Shows the user their current balance, the
// shortfall to the match bet, and a single CTA that closes the
// modal and navigates to /wallet so they can top up.
//
// Follows the platform's pop-up conventions:
//   - red × top-right to dismiss
//   - spring-in via motion.dev
//   - gold-border / purple-gradient panel identical to the Ludo
//     quit-confirm so the modal feels native across games.
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import Icon from './Icon';

type Props = {
  open: boolean;
  required: number;          // bet amount the user tried to join with
  balance: number;           // their current wallet balance
  onClose: () => void;
};

const fmt = (n: number) => n.toLocaleString('en-IN', { maximumFractionDigits: 0 });

export default function InsufficientFundsModal({ open, required, balance, onClose }: Props) {
  const nav = useNavigate();
  const shortfall = Math.max(0, (required | 0) - (balance | 0));

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[9999] bg-black/75 flex items-center justify-center p-4"
          onClick={onClose}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
        >
          <motion.div
            className="relative w-full max-w-[360px] bg-gradient-to-b from-[#2a0845] via-[#1a0333] to-[#0a0118] rounded-2xl border-[3px] border-[#d4af37] shadow-[0_20px_60px_rgba(0,0,0,0.9)] p-6 flex flex-col items-center gap-3"
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, scale: 0.88 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.92 }}
            transition={{ type: 'spring', stiffness: 360, damping: 28 }}
          >
            <button
              type="button"
              aria-label="Close"
              onClick={onClose}
              className="absolute -top-3 -right-3 w-10 h-10 bg-gradient-to-b from-[#e63946] to-[#900c16] border-2 border-[#d4af37] rounded-full flex items-center justify-center text-white font-black text-xl shadow-lg hover:scale-110 active:scale-95 transition-transform z-10"
            >
              ✕
            </button>

            <Icon
              name="account_balance_wallet"
              size={56}
              filled
              className="text-[#fde08b] drop-shadow-[0_4px_10px_rgba(0,0,0,0.5)]"
            />

            <h2 className="text-center leading-tight">
              <span className="block text-[10px] tracking-[0.3em] uppercase text-[#f8e7b3]">Not Enough</span>
              <span className="block text-2xl font-black mt-1 text-white">BALANCE</span>
            </h2>

            <p className="text-center text-[13px] text-[#dfff7a] max-w-[280px]">
              You need{fmt(required)} to start this match.
            </p>

            <div className="w-full rounded-xl bg-black/40 border border-[#bc9648] px-4 py-3 flex flex-col gap-1">
              <div className="flex items-center justify-between text-xs">
                <span className="text-white/70">Your balance</span>
                <span className="font-black text-white tabular-nums"> {fmt(balance | 0)}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-[#fca5a5]">Shortfall</span>
                <span className="font-black text-[#fca5a5] tabular-nums"> {fmt(shortfall)}</span>
              </div>
            </div>

            <button
              type="button"
              onClick={() => { onClose(); nav('/wallet'); }}
              className="w-full py-3 rounded-lg border-2 border-[#bc9648] bg-gradient-to-b from-[#fde08b] to-[#d4af37] text-black font-black tracking-widest text-sm shadow-md hover:brightness-110 active:scale-[0.98] transition-all flex items-center justify-center gap-2"
            >
              <Icon name="add_circle" size={18} filled />
              RECHARGE WALLET
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
